import { ArrowRight, Check, Clock3, Cpu, Diamond, Gauge, MemoryStick, Minus, Pause, Plus, Rocket, Volume2, VolumeX } from "lucide-react"
import { observer } from "mobx-react"
import { memo, type ReactNode, useEffect, useState } from "react"
import { useQueueModeHostResources, type HostMetricView } from "./queueModeHostResources"
import { QueueModeContinueButton } from "./QueueModeInputControls"
import { queueModeManager } from "./queueModeManager"

export function formatQueueRunTime(elapsedMs: number): string {
    const elapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
    const hours = Math.floor(elapsedSeconds / 3600)
    const minutes = Math.floor((elapsedSeconds % 3600) / 60)
    const seconds = elapsedSeconds % 60
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

interface QueueModeHudProps {
    title?: ReactNode
    isMajor: boolean
    onToggleMajor: () => void
}

export const QueueModeHud = observer(function QueueModeHud({ title, isMajor, onToggleMajor }: QueueModeHudProps): JSX.Element | null {
    const [nowMs, setNowMs] = useState(Date.now())
    const hostResources = useQueueModeHostResources(queueModeManager.isActive)

    useEffect(() => {
        if (!queueModeManager.isActive || !queueModeManager.isTimerRunning) return
        setNowMs(Date.now())
        const interval = window.setInterval(() => setNowMs(Date.now()), 1000)
        return () => window.clearInterval(interval)
    }, [queueModeManager.isActive, queueModeManager.isTimerRunning])

    if (!queueModeManager.isActive) return null

    const elapsed = formatQueueRunTime(queueModeManager.getElapsedMs(nowMs))
    const timerPauseReason = queueModeManager.isTimerRunning ? null : queueModeManager.isIdle ? "idle" : "app out of focus"
    const runLabel =
        queueModeManager.advanceAtMs === null
            ? queueModeManager.isBrowsingUnavailableTask
                ? "Q//NEXT"
                : queueModeManager.isWaitingForCapacity
                  ? "Q//CHILL"
                  : "Q//RUN"
            : "Q//CHAIN"

    const runStatus =
        queueModeManager.advanceAtMs !== null
            ? "Chain commands"
            : queueModeManager.isBrowsingUnavailableTask
              ? "Pick your next task"
              : queueModeManager.isWaitingForCapacity
                ? "Waiting for a slot"
                : queueModeManager.eligibleTaskCount === 0
                  ? "Queue clear"
                  : "Ready for your next move"

    return (
        <section
            className="queue-cockpit"
            data-queue-mode-hud="true"
            data-timer-running={queueModeManager.isTimerRunning}
            data-chaining={queueModeManager.advanceAtMs !== null}
            aria-label="Queue mission control"
        >
            <div className="queue-cockpit-deck">
                <div className="queue-cockpit-masthead">
                    {title ? <div className="queue-cockpit-title">{title}</div> : null}
                    <div className="queue-cockpit-identity" title={runStatus}>
                        <span className="queue-cockpit-emblem" aria-hidden="true">
                            <Rocket size={36} strokeWidth={1.5} />
                        </span>
                        <div className="queue-cockpit-brand">
                            <strong>Q MODE</strong>
                            <span className="queue-cockpit-mode" aria-label={runStatus}>
                                <span className="queue-cockpit-beacon" aria-hidden="true" />
                                {runLabel}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="queue-cockpit-instruments">
                    <div
                        className="queue-cockpit-score queue-cockpit-clock"
                        aria-label={`Today's queue time ${elapsed}${timerPauseReason ? `, paused while ${timerPauseReason}` : ""}`}
                        title={timerPauseReason ? `Today's queue timer is paused while ${timerPauseReason}` : "Today's active queue time"}
                    >
                        <span className="queue-cockpit-label">
                            <Clock3 size={12} aria-hidden="true" /> FOCUS TODAY
                        </span>
                        <span className="queue-cockpit-reading">
                            <ArcadeReadout value={elapsed} />
                            {timerPauseReason ? <Pause size={10} className="text-muted" aria-hidden="true" /> : null}
                        </span>
                    </div>
                    <div
                        className="queue-cockpit-score queue-cockpit-cleared"
                        aria-label={`${queueModeManager.handledTaskCount} handled`}
                        title={`Today: ${queueModeManager.launchedTaskCount} launched · ${queueModeManager.snoozedTaskCount} put to sleep · ${queueModeManager.closedTaskCount} closed`}
                    >
                        <span className="queue-cockpit-label">
                            <Check size={12} aria-hidden="true" />
                            <span>CLEARED TODAY</span>
                        </span>
                        <span
                            key={queueModeManager.handledTaskCount}
                            className="queue-cockpit-score-feedback"
                            data-score-pop={queueModeManager.handledTaskCount > 0}
                        >
                            <ArcadeReadout value={queueModeManager.handledTaskCount.toString().padStart(2, "0")} />
                        </span>
                    </div>
                    <div className="queue-cockpit-score queue-cockpit-ready" aria-label={`${queueModeManager.eligibleTaskCount} ready`}>
                        <span className="queue-cockpit-label">
                            <ArrowRight size={12} aria-hidden="true" />
                            <span>READY</span>
                        </span>
                        <span className="queue-cockpit-reading">
                            <ArcadeReadout value={queueModeManager.eligibleTaskCount.toString().padStart(2, "0")} />
                            {queueModeManager.majorReadyTaskCount > 0 ? (
                                <span className="queue-cockpit-priority-count" aria-label={`${queueModeManager.majorReadyTaskCount} priority ready`}>
                                    <Diamond size={9} fill="currentColor" aria-hidden="true" />
                                    {queueModeManager.majorReadyTaskCount}
                                </span>
                            ) : null}
                        </span>
                    </div>
                </div>
            </div>

            <div className="queue-cockpit-controls">
                <button
                    type="button"
                    className="btn queue-cockpit-priority"
                    onClick={onToggleMajor}
                    aria-label={isMajor ? "Make current task Regular" : "Make current task Priority"}
                    aria-pressed={isMajor}
                    title={
                        isMajor
                            ? "Priority tasks return before Regular tasks when ready. Make this task Regular."
                            : "Make this task Priority. Q returns to Priority tasks before Regular tasks when ready."
                    }
                >
                    <Diamond size={15} fill={isMajor ? "currentColor" : "none"} aria-hidden="true" />
                    <span>{isMajor ? "PRIORITY" : "REGULAR"}</span>
                </button>

                <div
                    className="queue-cockpit-capacity"
                    data-full={!queueModeManager.hasCapacity}
                    aria-label={`${queueModeManager.runningTaskCount} of ${queueModeManager.maxConcurrent} concurrent tasks`}
                >
                    <Gauge size={18} className="queue-cockpit-capacity-icon" aria-hidden="true" />
                    <div className="queue-cockpit-capacity-readout">
                        <span className="queue-cockpit-capacity-label">
                            <strong>{queueModeManager.runningTaskCount}</strong> running <span className="queue-cockpit-capacity-divider">/</span>{" "}
                            <strong>{queueModeManager.maxConcurrent}</strong> limit
                        </span>
                        <div
                            className="queue-cockpit-capacity-meter"
                            role="meter"
                            aria-label="Task capacity used"
                            aria-valuemin={0}
                            aria-valuemax={queueModeManager.maxConcurrent}
                            aria-valuenow={Math.min(queueModeManager.runningTaskCount, queueModeManager.maxConcurrent)}
                            aria-valuetext={`${queueModeManager.runningTaskCount} running, limit ${queueModeManager.maxConcurrent}`}
                        >
                            {Array.from({ length: 12 }, (_, index) => (
                                <span key={index} data-lit={index / 12 < queueModeManager.runningTaskCount / queueModeManager.maxConcurrent} />
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn queue-cockpit-step"
                        onClick={() => queueModeManager.setMaxConcurrent(queueModeManager.maxConcurrent - 1)}
                        disabled={queueModeManager.maxConcurrent <= 1}
                        aria-label="Decrease max concurrent tasks"
                        title="Decrease max concurrent tasks"
                    >
                        <Minus size={14} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="btn queue-cockpit-step"
                        onClick={() => queueModeManager.setMaxConcurrent(queueModeManager.maxConcurrent + 1)}
                        disabled={queueModeManager.maxConcurrent >= 99}
                        aria-label="Increase max concurrent tasks"
                        title="Increase max concurrent tasks"
                    >
                        <Plus size={14} aria-hidden="true" />
                    </button>
                </div>
                <div className="queue-cockpit-telemetry" aria-label="Connected computer resources">
                    <HostResourceCell metric={hostResources.cpu} icon={Cpu} label="CPU" />
                    <HostResourceCell metric={hostResources.memory} icon={MemoryStick} label="RAM" />
                </div>
                <button
                    type="button"
                    className="btn queue-cockpit-sounds"
                    onClick={() => queueModeManager.setSoundsEnabled(!queueModeManager.soundsEnabled)}
                    aria-label="Q mode sounds"
                    aria-pressed={queueModeManager.soundsEnabled}
                    title={queueModeManager.soundsEnabled ? "Mute Q mode sounds" : "Enable Q mode sounds"}
                >
                    {queueModeManager.soundsEnabled ? <Volume2 size={16} aria-hidden="true" /> : <VolumeX size={16} aria-hidden="true" />}
                </button>
                <button type="button" className="btn queue-cockpit-pause" onClick={queueModeManager.stop} title="Pause Q mode" aria-label="Pause queue mode">
                    <Pause size={14} fill="currentColor" aria-hidden="true" />
                    <span>PAUSE</span>
                </button>
            </div>
        </section>
    )
})

// Clock and counts share the same seven-segment display; values remain real Q stats.
const DIGIT_SEGMENTS = [0x3f, 0x06, 0x5b, 0x4f, 0x66, 0x6d, 0x7d, 0x07, 0x7f, 0x6f]
const SEGMENT_SHAPES = [
    "4,1 16,1 18,3 15,5 5,5 2,3",
    "17,4 19,3 19,14 17,16 15,14 15,6",
    "17,17 19,19 19,30 17,31 15,28 15,20",
    "5,29 15,29 18,32 16,33 4,33 2,31",
    "1,19 3,17 5,20 5,28 3,30 1,31",
    "1,3 3,4 5,6 5,14 3,16 1,14",
    "4,15 16,15 18,17 16,19 4,19 2,17",
]

const ArcadeReadout = memo(function ArcadeReadout({ value }: { value: string }): JSX.Element {
    return (
        <span className="queue-cockpit-value" aria-hidden="true">
            {Array.from(value, (character, position) =>
                character === ":" ? (
                    <svg className="queue-cockpit-colon" viewBox="0 0 8 34" key={position}>
                        <path d="M2 9h4v4H2z M2 22h4v4H2z" fill="currentColor" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 20 34" key={position}>
                        {SEGMENT_SHAPES.map((points, segment) => (
                            <polygon key={segment} points={points} data-lit={((DIGIT_SEGMENTS[Number(character)] ?? 0) & (1 << segment)) !== 0} />
                        ))}
                    </svg>
                )
            )}
        </span>
    )
})

function HostResourceCell({ metric, icon: Icon, label }: { metric: HostMetricView; icon: typeof Cpu; label: string }): JSX.Element {
    return (
        <div className="queue-cockpit-host" data-stale={!metric.available || metric.stale} aria-label={metric.ariaLabel} title={metric.title}>
            <Icon size={12} aria-hidden="true" />
            <span>{label}</span>
            <strong>{metric.compact}</strong>
            {metric.stale ? (
                <span className="queue-cockpit-stale" aria-hidden="true">
                    *
                </span>
            ) : null}
        </div>
    )
}

export const QueueModeHandoffBanner = observer(function QueueModeHandoffBanner({ onContinue }: { onContinue: () => void }): JSX.Element | null {
    const [nowMs, setNowMs] = useState(Date.now())
    const advanceAtMs = queueModeManager.advanceAtMs

    useEffect(() => {
        if (!queueModeManager.isActive || advanceAtMs === null) return
        let timeout: number | undefined
        const tick = () => {
            const now = Date.now()
            setNowMs(now)
            const remainingMs = advanceAtMs - now
            // Match the actual deadline even when mounted midway through a second.
            if (remainingMs > 0) timeout = window.setTimeout(tick, remainingMs % 1000 || 1000)
        }
        tick()
        return () => {
            if (timeout !== undefined) window.clearTimeout(timeout)
        }
    }, [advanceAtMs, queueModeManager.isActive])

    if (!queueModeManager.isActive) return null

    if (advanceAtMs !== null) {
        const countdownSeconds = Math.max(1, Math.ceil((advanceAtMs - nowMs) / 1000))
        return (
            <div
                data-queue-mode-handoff="countdown"
                className="relative flex min-h-[76px] flex-none items-center justify-center overflow-hidden border-y-2 border-primary/55 bg-primary/10 px-4 font-mono shadow-inner shadow-primary/10 @max-[720px]:min-h-16"
                aria-label={`Next task in ${countdownSeconds} seconds. Chain commands now.`}
            >
                <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-primary" />
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-primary/70" />
                <div className="pointer-events-none absolute left-4 top-2 text-[9px] tracking-[0.2em] text-primary/65 @max-[720px]:hidden">
                    HANDOFF SEQUENCE
                </div>
                <div className="pointer-events-none absolute right-4 top-2 text-[9px] tracking-[0.2em] text-primary/65 @max-[720px]:hidden">
                    QUEUE DRIVE // LIVE
                </div>

                <div className="flex items-center gap-4 text-primary @max-[720px]:gap-2.5">
                    <div className="text-right @max-[720px]:hidden">
                        <div className="text-[9px] tracking-[0.24em] text-muted">NEXT TASK</div>
                        <div className="text-xs font-bold tracking-[0.18em]">INCOMING</div>
                    </div>
                    <div className="relative flex h-14 min-w-[74px] items-center justify-center border-2 border-primary bg-base-100 px-2 shadow-lg shadow-primary/25 @max-[720px]:h-12 @max-[720px]:min-w-16">
                        <span className="absolute left-1 top-1 h-1.5 w-1.5 border-l border-t border-primary" />
                        <span className="absolute bottom-1 right-1 h-1.5 w-1.5 border-b border-r border-primary" />
                        <span key={countdownSeconds} className="text-[42px] font-black leading-none tabular-nums tracking-[-0.08em] @max-[720px]:text-4xl">
                            {countdownSeconds.toString().padStart(2, "0")}
                        </span>
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.22em] text-muted">SECONDS</div>
                        <div className="text-sm font-black tracking-[0.16em] @max-[720px]:text-xs">// CHAIN COMMANDS</div>
                        <div className="mt-1 flex h-1.5 w-44 gap-1 @max-[720px]:w-28" aria-hidden="true">
                            {Array.from({ length: 3 }, (_, index) => (
                                <span
                                    key={index}
                                    className={`h-full flex-1 bg-primary shadow-sm shadow-primary/40 transition-opacity duration-150 motion-reduce:transition-none ${index < countdownSeconds ? "opacity-100" : "opacity-15"}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (queueModeManager.isBrowsingUnavailableTask) {
        const hasReadyTask = queueModeManager.eligibleTaskCount > 0
        return (
            <div
                data-queue-mode-handoff="unavailable"
                className="queue-mode-rollout flex min-h-10 flex-none items-center justify-between gap-3 border-b border-info/35 bg-info/5 px-3 font-mono @max-[600px]:min-h-12 @max-[600px]:gap-2 @max-[600px]:px-2"
                aria-label="Current task is unavailable in queue mode"
            >
                <span className="text-[11px] leading-4 text-muted">This task is already running or closed.</span>
                <QueueModeContinueButton onContinue={onContinue} disabled={!hasReadyTask} />
            </div>
        )
    }

    if (!queueModeManager.isWaitingForCapacity) return null

    return (
        <div
            data-queue-mode-handoff="capacity"
            className="relative flex min-h-16 flex-none items-center justify-center overflow-hidden border-y-2 border-warning/55 bg-warning/10 px-4 font-mono shadow-inner shadow-warning/10"
            aria-label={`Concurrency full. ${queueModeManager.runningTaskCount} of ${queueModeManager.maxConcurrent} tasks running.`}
        >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-warning" />
            <div className="flex items-center gap-4 text-warning @max-[720px]:gap-2.5">
                <Gauge className="h-7 w-7 animate-pulse motion-reduce:animate-none @max-[720px]:h-6 @max-[720px]:w-6" />
                <div>
                    <div className="text-[9px] tracking-[0.22em] text-muted">CAPACITY HOLD // AUTO-RESUME ARMED</div>
                    <div className="text-base font-black tracking-[0.12em] @max-[720px]:text-sm">YOU'RE CHILLING</div>
                </div>
                <div className="border-l border-warning/35 pl-4 text-center @max-[720px]:pl-2">
                    <div className="text-xl font-black tabular-nums">
                        {queueModeManager.runningTaskCount}/{queueModeManager.maxConcurrent}
                    </div>
                    <div className="text-[8px] tracking-[0.18em] text-muted">LIVE / MAX</div>
                </div>
                <button
                    type="button"
                    className="btn flex h-12 min-w-52 items-center justify-center gap-2 border border-warning/45 bg-warning/10 px-4 text-[10px] font-bold tracking-[0.12em] text-warning shadow-sm hover:bg-warning/20 @max-[720px]:h-11 @max-[720px]:min-w-40 @max-[720px]:px-3"
                    onClick={() => queueModeManager.setMaxConcurrent(queueModeManager.maxConcurrent + 1)}
                    disabled={queueModeManager.maxConcurrent >= 99}
                    aria-label="Add one max-concurrent slot"
                >
                    <Plus className="h-3.5 w-3.5 flex-none" />
                    <span>ADD CAPACITY</span>
                </button>
            </div>
        </div>
    )
})
